<script setup lang="ts">
import dayjs from 'dayjs'

import ActivityCardBase from './ActivityCardBase.vue'
import type { FeedActivity } from '@/types/customer/ActivitiesFeed'

const { t, locale } = useI18n()

const isExpanded = ref(false)

const formatDate = (dateString: string) => dayjs(dateString).locale(locale.value).format('DD/MM/YYYY')
const formatTime = (dateString: string) => dayjs(dateString).locale(locale.value).format('HH:mm')

const props = defineProps<{
  activity: FeedActivity
  showConnector?: boolean
}>()

const lastEvent = computed(() => {
  const events = props.activity.data?.email_events ?? []
  if (!events.length) return null

  return [...events].sort((a, b) => dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf())[0]
})

const currentStatusLabel = computed(() => {
  if (!lastEvent.value) return null

  const key = `pages.customer.customerDetailPage.blocks.activity.common.detail.status.${lastEvent.value.event_type}`

  return t(key)
})

const statusColorMap: Record<string, string> = {
  delivered: 'green',
  bounced: 'red',
  opened: 'blue',
  clicked: 'indigo'
}

const currentStatusColor = computed(() => {
  return statusColorMap[lastEvent.value?.event_type ?? ''] ?? 'gray'
})

const clickedCount = computed(() => {
  const events = props.activity.data?.email_events ?? []
  return events.filter((e) => e.event_type === 'clicked').length
})
</script>

<template>
  <ActivityCardBase
    icon="HeroEnvelopeIconSolid"
    icon-color="bg-green-500"
    :occurred-at="activity.occurred_at"
    :show-connector="showConnector"
  >
    <template #description>
      <div class="space-y-0.4">
        <p class="text-sm text-muted-foreground">
          {{ t('pages.customer.customerDetailPage.blocks.activity.common.text.sent.mail') }}
        </p>

        <button
          type="button"
          class="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground-secondary focus:outline-hidden cursor-pointer duration-300"
          @click="isExpanded = !isExpanded"
        >
          <span>{{ t('pages.customer.customerDetailPage.blocks.activity.common.detail.seeDetails') }}</span>
          <BaseIcon
            name="HeroChevronDownIconSolid"
            class="h-4 w-4 transition-transform duration-200"
            :class="{ 'rotate-180': isExpanded }"
          />
        </button>
      </div>
    </template>

    <template #footer>
      <div v-show="isExpanded" class="mt-4 self-stretch rounded-lg border border-border bg-white p-4 ml-8 space-y-2">
        <div v-if="currentStatusLabel">
          <div class="self-stretch justify-center text-sm font-medium text-muted-foreground">
            {{ t('pages.customer.customerDetailPage.blocks.activity.common.detail.statusLabel') }}
          </div>
          <BaseChip class="mt-2" :text="currentStatusLabel" :color="currentStatusColor" />
        </div>

        <div>
          <div class="self-stretch justify-center text-sm font-medium text-muted-foreground">
            {{ t('pages.marketingFlow.marketingFlowPerformance.statistics.emailLinksClicked') }}
          </div>
          <BaseChip class="mt-2" :text="clickedCount.toString()" color="gray" />
        </div>
      </div>
    </template>

    <template #date>
      <div>{{ formatDate(activity.occurred_at) }}</div>
      <div>{{ formatTime(activity.occurred_at) }}</div>
    </template>
  </ActivityCardBase>
</template>
